<script setup lang="ts">
import { mdiMenu } from '@mdi/js'

defineOptions({ name: 'AppHeader' })

const { trackCta } = useCtaEvent()

// #755: 公開ヘッダーをログイン状態に応じて分岐する。ログイン済みなら「サインイン /
// 冒険を始める」を単一の「冒険を続ける」(→ /adventurerscamp) に置換する（定石 = マーケ
// ヘッダーはログイン済みに Login/Signup を見せず、アプリへ戻す単一 CTA に畳む）。
//
// 認証は localStorage 永続（#596）で **SSR は状態を知り得ない**（詳細と cookie 回帰を
// 却下した理由は doc/architecture/authentication.md の「認証状態の永続化モデル」）。
// よって auth 依存のボタン領域だけを <ClientOnly> にし、判定確定まで同幅プレースホルダで
// 空間を予約する（未ログイン UI を描いてから差し替える＝誤クリック/不安/CLS を避ける）。
// ロゴと「使い方」は状態非依存なので SSR のまま出す（SEO/LCP を損なわない）。
//
// リアクティブに追従させるため、スナップショットの useAuthInfo() ではなくストアの
// computed を使う（app.vue と同じパターン）。
const authInfoStore = useAuthInfoStore()
const isAuthenticated = computed(() => authInfoStore.isAuthenticated)
</script>

<template>
  <v-app-bar
    color="#FFFFFF00"
    app
    elevation="0"
  >
    <!-- Logo — v-app-bar-title はデフォルトで flex: 1 1 auto なため、
         そのまま NuxtLink を包むとリンクのクリック判定がロゴ画像を大きく
         超えて右側の空白まで広がってしまう。`.logo-title` で width を
         コンテンツ幅に絞り、リンク自体も inline-flex でロゴ画像にぴたり
         合わせる。 -->
    <v-app-bar-title class="logo-title">
      <NuxtLink
        to="/"
        class="logo-link"
      >
        <BrandTextLogo
          class="d-none d-md-flex"
          style="height: 48px;"
        />
        <BrandTextLogo
          class="d-none d-sm-flex d-md-none"
          style="height: 30px;"
        />
        <img
          class="d-sm-none shadowed"
          src="@/assets/images/code_and_magic_black_iconlogo.svg"
          alt="Code & Magic"
          width="auto"
          height="40px"
          fetchpriority="high"
          decoding="async"
        >
      </NuxtLink>
    </v-app-bar-title>

    <v-spacer />

    <!-- 使い方: ログイン状態に依存しないので SSR のまま出す -->
    <v-btn
      class="d-none d-sm-flex mr-2 sub-button"
      variant="text"
      to="/articles"
    >
      使い方
    </v-btn>

    <!-- 認証依存の CTA 領域: SSR は状態不明なので ClientOnly + 同幅プレースホルダ -->
    <ClientOnly>
      <!-- ログイン済み: 単一の「冒険を続ける」でアプリ(冒険者のキャンプ)へ戻す -->
      <v-btn
        v-if="isAuthenticated"
        class="mr-2 main-button"
        variant="outlined"
        to="/adventurerscamp"
        @click="trackCta('冒険を続ける（header）')"
      >
        冒険を続ける
      </v-btn>

      <!-- 未ログイン: サインイン(デスクトップのみ) + 冒険を始める -->
      <template v-else>
        <v-btn
          class="d-none d-sm-flex mr-2 main-button"
          variant="outlined"
          to="/signin"
        >
          サインイン
        </v-btn>
        <v-btn
          class="mr-2 main-button"
          variant="outlined"
          to="/signup"
          @click="trackCta('冒険を始める（header）')"
        >
          冒険を始める
        </v-btn>
      </template>

      <!-- 判定確定まで(SSR/ハイドレーション中)は同幅プレースホルダで空間を予約 -->
      <template #fallback>
        <div
          class="auth-cta-placeholder mr-2"
          aria-hidden="true"
        />
      </template>
    </ClientOnly>

    <!-- Mobile menu -->
    <template #append>
      <v-menu location="bottom right">
        <template #activator="{ props }">
          <v-btn
            class="d-sm-none shadowed"
            icon
            aria-label="メニュー"
            v-bind="props"
          >
            <v-icon>{{ mdiMenu }}</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            to="/articles"
            title="使い方"
          />
          <!-- v-menu の中身は開くまで描画されないため（Vuetify 3 は遅延描画）、
               ここは ClientOnly 不要。ハイドレーション後の reactive 判定で十分。 -->
          <v-list-item
            v-if="isAuthenticated"
            to="/adventurerscamp"
            title="冒険を続ける"
          />
          <v-list-item
            v-else
            to="/signin"
            title="サインイン"
          />
        </v-list>
      </v-menu>
    </template>
  </v-app-bar>
</template>

<style scoped>
/* ロゴ部分のクリック判定をロゴ画像の実寸に揃える。
   - .logo-title: v-app-bar-title のデフォルト flex: 1 1 auto を抑え、
     コンテンツ幅 (= ロゴ + 左右マージン) までしか伸びないようにする
   - .logo-link: NuxtLink (= <a>) を inline-flex にしてロゴ画像にぴたり
     合わせ、クリック領域が右側の空白に滲み出さないようにする */
.logo-title {
  flex: 0 0 auto;
}

.logo-link {
  display: inline-flex;
  align-items: center;
}

.shadowed {
  margin: 6px;
  filter: invert(1) drop-shadow(#000000 0 0 4px);
}

.main-button {
  font-weight: bold;
  color: white;
  text-shadow: 0 0 6px #000000ff;
  box-shadow: 0 0 6px #00000088;
}

.main-button:hover {
  background-color: #ffffff80;
}

.sub-button {
  font-weight: bold;
  color: white;
  text-shadow: 0 0 6px #000000ff;
}

.sub-button:hover {
  background-color: #ffffff80;
}

/* #755: auth 判定確定まで CTA 領域の幅を予約して CLS/チラつきを抑える。
   プライマリボタン (冒険を始める/続ける) 相当の幅・高さの透明な箱。 */
.auth-cta-placeholder {
  width: 148px;
  height: 36px;
}
</style>
